<template>
  <div class="hub-page">
    <!-- TOPBAR -->
    <header class="hub-topbar">
      <div class="brand">
        <img :src="`${publicPath}logo.png`" alt="INVOXIS" class="brand-logo" />
      </div>
      <div class="topbar-right">
        <div class="user-menu-wrap" ref="userMenu">
          <button class="user-chip" @click="toggleMenu" :class="{ 'is-open': menuOpen }">
            <div class="user-avatar">{{ userInitial }}</div>
            <span class="user-name">{{ username }}</span>
            <svg class="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>

          <transition name="menu-fade">
            <div v-if="menuOpen" class="user-menu">
              <div class="menu-user">
                <div class="menu-user-avatar">{{ userInitial }}</div>
                <div class="menu-user-info">
                  <div class="menu-user-name">{{ username }}</div>
                  <div class="menu-user-email" v-if="userProfile.email">{{ userProfile.email }}</div>
                </div>
              </div>
              <div class="menu-divider"></div>
              <button class="menu-item" @click="openAccount">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                My Account
              </button>
              <div class="menu-divider"></div>
              <button class="menu-item menu-item-danger" @click="logout">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                Sign out
              </button>
            </div>
          </transition>
        </div>
      </div>
    </header>

    <!-- MAIN CONTENT -->
    <main class="hub-content">
      <!-- HERO -->
      <div class="hero">
        <div class="hero-eyebrow">
          <span class="hero-eyebrow-dot"></span>
          Workspace
        </div>
        <h1 class="hero-title">Welcome back, {{ username }}</h1>
        <p class="hero-sub">Select a Property Management System to open its workspace, or connect a new one to your account.</p>
      </div>

      <!-- QUICK ACTIONS -->
      <div class="quick-actions">
        <button class="action-tile" @click="showAccountModal = true">
          <div class="action-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <div class="action-text">
            <div class="action-title">My Account</div>
            <div class="action-desc">Profile and password</div>
          </div>
          <div class="action-chevron">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        </button>

        <router-link v-if="isSuperAdmin" to="/admin" class="action-tile action-tile-admin">
          <div class="action-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
          <div class="action-text">
            <div class="action-title">Administration</div>
            <div class="action-desc">Manage users and access</div>
          </div>
          <span v-if="pendingUsersCount > 0" class="notification-badge">{{ pendingUsersCount }}</span>
          <div class="action-chevron">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        </router-link>
      </div>

      <!-- PMS SECTION -->
      <section class="pms-section">
        <div class="section-header">
          <div>
            <h3 class="section-title">Property Management Systems</h3>
            <p class="section-sub">{{ pmsConfigs.length }} {{ pmsConfigs.length === 1 ? 'system connected' : 'systems connected' }}</p>
          </div>
          <button @click="showAddPmsModal = true" class="btn-primary">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add PMS
          </button>
        </div>

        <!-- PMS Grid -->
        <div v-if="pmsConfigs.length > 0" class="pms-grid">
          <div v-for="pms in pmsConfigs" :key="pms.id" class="pms-card">
            <button class="delete-pms-btn" @click.stop="confirmDeletePms(pms)" title="Delete PMS">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
            </button>
            <div class="pms-card-content" @click="selectPms(pms)">
              <div class="pms-icon">
                <svg v-if="pms.pms_type === 'rentlio'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                <svg v-else-if="pms.pms_type === 'booking'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 8h5a2.5 2.5 0 0 1 0 5H8zM8 13h6a2.5 2.5 0 0 1 0 5H8z"/></svg>
                <svg v-else-if="pms.pms_type === 'airbnb'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2C7 8 5 12 5 15a7 7 0 0 0 14 0c0-3-2-7-7-13z"/></svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="9" x2="15" y2="9"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>
              </div>
              <div class="pms-info">
                <h4 class="pms-name">{{ pms.name }}</h4>
                <p class="pms-type">{{ formatPmsType(pms.pms_type) }}</p>
              </div>
              <div class="pms-status" :class="{ 'active': pms.is_active }">
                <span class="status-dot"></span>
                {{ pms.is_active ? 'Active' : 'Inactive' }}
              </div>
              <div class="pms-arrow">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <div class="empty-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>
          </div>
          <h4>No PMS connected</h4>
          <p>Add your first Property Management System to start syncing property data.</p>
          <button @click="showAddPmsModal = true" class="btn-primary">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add your first PMS
          </button>
        </div>
      </section>
    </main>

    <!-- Add PMS Modal -->
    <div v-if="showAddPmsModal" class="modal-overlay" @click.self="showAddPmsModal = false">
      <div class="modal-container">
        <div class="modal-header">
          <h3>Add New PMS</h3>
          <button @click="showAddPmsModal = false" class="close-btn" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="modal-steps">
          <div class="step" :class="{ 'active': addPmsStep >= 1 }">
            <span class="step-num">1</span>
            <span class="step-label">Select PMS</span>
          </div>
          <div class="step-divider"></div>
          <div class="step" :class="{ 'active': addPmsStep >= 2 }">
            <span class="step-num">2</span>
            <span class="step-label">Credentials</span>
          </div>
        </div>

        <div class="modal-body">
          <!-- Step 1: Select PMS Type -->
          <div v-if="addPmsStep === 1" class="pms-selection">
            <p class="step-info">Choose the platform you want to connect.</p>
            <div class="pms-options">
              <div class="pms-option" :class="{ 'selected': selectedPmsType === 'rentlio' }" @click="selectedPmsType = 'rentlio'">
                <div class="option-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                </div>
                <span class="option-name">Rentlio</span>
                <span class="option-check" v-if="selectedPmsType === 'rentlio'">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
              </div>
              <!--
              Lodgify — masqué temporairement pendant que le setup se finalise.
              À réactiver en enlevant les balises de commentaire.
              <div class="pms-option" :class="{ 'selected': selectedPmsType === 'lodgify' }" @click="selectedPmsType = 'lodgify'">
                <div class="option-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12h20"/><path d="M12 2v20"/><circle cx="12" cy="12" r="10"/></svg>
                </div>
                <span class="option-name">Lodgify</span>
                <span class="option-check" v-if="selectedPmsType === 'lodgify'">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
              </div>
              -->

              <div class="pms-option disabled" title="Coming soon">
                <div class="option-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 8h5a2.5 2.5 0 0 1 0 5H8zM8 13h6a2.5 2.5 0 0 1 0 5H8z"/></svg>
                </div>
                <span class="option-name">Booking.com</span>
                <span class="coming-soon">Soon</span>
              </div>
              <div class="pms-option disabled" title="Coming soon">
                <div class="option-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2C7 8 5 12 5 15a7 7 0 0 0 14 0c0-3-2-7-7-13z"/></svg>
                </div>
                <span class="option-name">Airbnb</span>
                <span class="coming-soon">Soon</span>
              </div>
            </div>
            <button @click="addPmsStep = 2" class="btn-primary btn-full" :disabled="!selectedPmsType">
              Continue
            </button>
          </div>

          <!-- Step 2: Enter Credentials -->
          <div v-if="addPmsStep === 2" class="credentials-form">
            <p class="step-info">Enter your {{ formatPmsType(selectedPmsType) }} API credentials.</p>

            <div class="form-group">
              <label for="pmsName">Configuration name</label>
              <input type="text" id="pmsName" v-model="newPmsName" placeholder="e.g. My Properties" class="form-input" />
            </div>

            <div class="form-group">
              <label for="apiKey">API key</label>
              <input type="password" id="apiKey" v-model="newPmsApiKey" placeholder="Paste your API key" class="form-input" />
            </div>

            <div v-if="addPmsError" class="alert alert-error">{{ addPmsError }}</div>
            <div v-if="addPmsSuccess" class="alert alert-success">{{ addPmsSuccess }}</div>

            <div class="form-actions">
              <button @click="addPmsStep = 1" class="btn-secondary">Back</button>
              <button @click="testConnection" class="btn-outline" :disabled="isTestingConnection || !newPmsName || !newPmsApiKey">
                <span v-if="isTestingConnection" class="loading-spinner"></span>
                {{ isTestingConnection ? 'Testing...' : 'Test connection' }}
              </button>
              <button @click="savePmsConfig" class="btn-primary" :disabled="!connectionTested || isSavingPms">
                <span v-if="isSavingPms" class="loading-spinner"></span>
                {{ isSavingPms ? 'Saving...' : 'Save' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Account Settings Modal -->
    <div v-if="showAccountModal" class="modal-overlay" @click.self="showAccountModal = false">
      <div class="modal-container account-modal">
        <div class="modal-header">
          <h3>My Account</h3>
          <button @click="showAccountModal = false" class="close-btn" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="accountSuccessMessage" class="alert alert-success">{{ accountSuccessMessage }}</div>
          <div v-if="accountErrorMessage" class="alert alert-error">{{ accountErrorMessage }}</div>

          <!-- Account Information -->
          <div class="account-section">
            <h4 class="subsection-title">Account information</h4>
            <div class="account-info-grid">
              <div class="info-item">
                <label>Username</label>
                <span>{{ userProfile.username }}</span>
              </div>
              <div class="info-item">
                <label>Email</label>
                <span>{{ userProfile.email }}</span>
              </div>
              <div class="info-item">
                <label>Status</label>
                <span class="status-badge" :class="userProfile.status">{{ userProfile.status }}</span>
              </div>
            </div>
          </div>

          <!-- Change Password -->
          <div class="password-section">
            <h4 class="subsection-title">Change password</h4>
            <div class="password-form">
              <div class="form-group">
                <label for="currentPassword">Current password</label>
                <div class="input-with-toggle">
                  <input :type="showCurrentPassword ? 'text' : 'password'" id="currentPassword" v-model="passwordForm.currentPassword" class="form-input" placeholder="Enter current password">
                  <button type="button" class="toggle-password" @click="showCurrentPassword = !showCurrentPassword" :aria-label="showCurrentPassword ? 'Hide password' : 'Show password'">
                    <svg v-if="showCurrentPassword" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  </button>
                </div>
              </div>
              <div class="form-group">
                <label for="newPassword">New password</label>
                <div class="input-with-toggle">
                  <input :type="showNewPassword ? 'text' : 'password'" id="newPassword" v-model="passwordForm.newPassword" class="form-input" placeholder="Minimum 8 characters">
                  <button type="button" class="toggle-password" @click="showNewPassword = !showNewPassword" :aria-label="showNewPassword ? 'Hide password' : 'Show password'">
                    <svg v-if="showNewPassword" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  </button>
                </div>
              </div>
              <div class="form-group">
                <label for="confirmNewPassword">Confirm new password</label>
                <div class="input-with-toggle">
                  <input :type="showConfirmNewPassword ? 'text' : 'password'" id="confirmNewPassword" v-model="passwordForm.confirmNewPassword" class="form-input" placeholder="Re-enter new password">
                  <button type="button" class="toggle-password" @click="showConfirmNewPassword = !showConfirmNewPassword" :aria-label="showConfirmNewPassword ? 'Hide password' : 'Show password'">
                    <svg v-if="showConfirmNewPassword" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  </button>
                </div>
              </div>
              <button @click="changePassword" class="btn-primary" :disabled="changingPassword || !canChangePassword">
                <span v-if="changingPassword" class="loading-spinner"></span>
                {{ changingPassword ? 'Changing...' : 'Change password' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
      <div class="modal-container delete-modal">
        <div class="modal-header">
          <h3>Delete PMS configuration</h3>
          <button @click="showDeleteModal = false" class="close-btn" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="warning-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </div>
          <p class="warning-text">Delete <strong>{{ pmsToDelete?.name }}</strong>?</p>
          <p class="warning-subtext">This action cannot be undone. All data associated with this PMS configuration will be permanently removed.</p>

          <div v-if="deleteError" class="alert alert-error">{{ deleteError }}</div>

          <div class="modal-actions">
            <button @click="showDeleteModal = false" class="btn-secondary">Cancel</button>
            <button @click="deletePms" class="btn-danger" :disabled="isDeletingPms">
              <span v-if="isDeletingPms" class="loading-spinner"></span>
              {{ isDeletingPms ? 'Deleting...' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SETTINGS from '@/config/settings.js';

export default {
  name: 'HubView',
  data() {
    return {
      publicPath: process.env.BASE_URL,
      username: '',
      isSuperAdmin: false,
      pendingUsersCount: 0,
      menuOpen: false,
      pmsConfigs: [],
      showAddPmsModal: false,
      addPmsStep: 1,
      selectedPmsType: 'rentlio',
      newPmsName: '',
      newPmsApiKey: '',
      addPmsError: '',
      addPmsSuccess: '',
      isTestingConnection: false,
      isSavingPms: false,
      connectionTested: false,
      // Account modal
      showAccountModal: false,
      userProfile: {
        username: '',
        email: '',
        status: ''
      },
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      },
      changingPassword: false,
      accountSuccessMessage: '',
      accountErrorMessage: '',
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmNewPassword: false,
      // Delete modal
      showDeleteModal: false,
      pmsToDelete: null,
      isDeletingPms: false,
      deleteError: ''
    };
  },

  computed: {
    canChangePassword() {
      return (
        this.passwordForm.currentPassword &&
        this.passwordForm.newPassword &&
        this.passwordForm.confirmNewPassword &&
        this.passwordForm.newPassword === this.passwordForm.confirmNewPassword &&
        this.passwordForm.newPassword.length >= 8
      );
    },
    userInitial() {
      return (this.username && this.username.charAt(0).toUpperCase()) || 'U';
    }
  },

  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    closeMenu() {
      this.menuOpen = false;
    },
    openAccount() {
      this.closeMenu();
      this.showAccountModal = true;
    },
    handleClickOutside(e) {
      if (this.$refs.userMenu && !this.$refs.userMenu.contains(e.target)) {
        this.closeMenu();
      }
    },
    handleKeydown(e) {
      if (e.key === 'Escape' && this.menuOpen) {
        this.closeMenu();
      }
    },
    logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('selectedPmsConfig');
      this.closeMenu();
      this.$router.push('/login');
    },

    formatPmsType(type) {
      const types = {
        'rentlio': 'Rentlio',
        'lodgify': 'Lodgify',
        'booking': 'Booking.com',
        'airbnb': 'Airbnb'
      };
      return types[type] || type;
    },

    selectPms(pms) {
      // Store selected PMS config
      localStorage.setItem('selectedPmsConfig', JSON.stringify(pms));
      // Redirect to dashboard
      this.$router.push('/dashboard');
    },

    async fetchUserProfile() {
      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      try {
        const apiUrl = this.getApiUrl('/api/users/profile/');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.username = data.username || data.email || 'User';
          this.isSuperAdmin = data.is_super_admin || false;

          // Update userProfile for modal
          this.userProfile = {
            username: data.username || '',
            email: data.email || '',
            status: data.status || 'active'
          };

          // Store user info
          localStorage.setItem('user', JSON.stringify(data));
        } else if (response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    },

    async changePassword() {
      if (!this.canChangePassword) return;

      this.changingPassword = true;
      this.accountSuccessMessage = '';
      this.accountErrorMessage = '';

      const token = localStorage.getItem('token');

      try {
        const apiUrl = this.getApiUrl('/api/users/change-password/');
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            current_password: this.passwordForm.currentPassword,
            new_password: this.passwordForm.newPassword
          })
        });

        if (response.ok) {
          this.accountSuccessMessage = 'Password changed successfully!';
          this.passwordForm = {
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: ''
          };

          setTimeout(() => {
            this.accountSuccessMessage = '';
          }, 3000);
        } else {
          const data = await response.json();
          this.accountErrorMessage = data.message || data.detail || 'Failed to change password.';
        }
      } catch (error) {
        console.error('Error changing password:', error);
        this.accountErrorMessage = 'Failed to change password. Please try again.';
      } finally {
        this.changingPassword = false;
      }
    },

    async fetchPmsConfigs() {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const apiUrl = this.getApiUrl('/api/users/pms-configs/');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.pmsConfigs = data.results || data || [];
        }
      } catch (error) {
        console.error('Error fetching PMS configs:', error);
      }
    },

    async fetchPendingUsers() {
      if (!this.isSuperAdmin) return;

      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const apiUrl = this.getApiUrl('/api/users/pending/');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.pendingUsersCount = data.length || 0;
        }
      } catch (error) {
        console.error('Error fetching pending users:', error);
      }
    },

    async testConnection() {
      this.addPmsError = '';
      this.addPmsSuccess = '';
      this.isTestingConnection = true;

      const token = localStorage.getItem('token');

      try {
        const apiUrl = this.getApiUrl('/api/pms/test-connection/');
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            pms_type: this.selectedPmsType,
            credentials: {
              api_key: this.newPmsApiKey
            }
          })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          this.connectionTested = true;
          this.addPmsSuccess = 'Connection successful! You can now save this configuration.';
        } else {
          this.addPmsError = data.message || 'Connection failed. Please check your credentials.';
          this.connectionTested = false;
        }
      } catch (error) {
        console.error('Connection test error:', error);
        this.addPmsError = 'Connection test failed. Please try again.';
        this.connectionTested = false;
      } finally {
        this.isTestingConnection = false;
      }
    },

    async savePmsConfig() {
      this.addPmsError = '';
      this.isSavingPms = true;

      const token = localStorage.getItem('token');

      try {
        const apiUrl = this.getApiUrl('/api/users/pms-configs/');
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: this.newPmsName,
            pms_type: this.selectedPmsType,
            credentials: {
              api_key: this.newPmsApiKey
            },
            is_active: true
          })
        });

        if (response.ok) {
          // Reset form
          this.showAddPmsModal = false;
          this.addPmsStep = 1;
          this.newPmsName = '';
          this.newPmsApiKey = '';
          this.connectionTested = false;
          this.addPmsSuccess = '';

          // Refresh PMS list
          await this.fetchPmsConfigs();
        } else {
          const data = await response.json();
          this.addPmsError = data.message || data.detail || 'Failed to save configuration.';
        }
      } catch (error) {
        console.error('Save PMS error:', error);
        this.addPmsError = 'Failed to save configuration. Please try again.';
      } finally {
        this.isSavingPms = false;
      }
    },

    confirmDeletePms(pms) {
      this.pmsToDelete = pms;
      this.showDeleteModal = true;
      this.deleteError = '';
    },

    async deletePms() {
      if (!this.pmsToDelete) return;

      this.isDeletingPms = true;
      this.deleteError = '';

      const token = localStorage.getItem('token');

      try {
        const apiUrl = this.getApiUrl(`/api/users/pms-configs/${this.pmsToDelete.id}/`);
        const response = await fetch(apiUrl, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          // Close modal
          this.showDeleteModal = false;
          this.pmsToDelete = null;

          // Refresh PMS list
          await this.fetchPmsConfigs();
        } else {
          const data = await response.json();
          this.deleteError = data.message || data.detail || 'Failed to delete PMS configuration.';
        }
      } catch (error) {
        console.error('Delete PMS error:', error);
        this.deleteError = 'Failed to delete PMS configuration. Please try again.';
      } finally {
        this.isDeletingPms = false;
      }
    },

    getApiUrl(endpoint) {
      return `${SETTINGS.API.baseUrl}${endpoint}`;
    }
  },

  async mounted() {
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('keydown', this.handleKeydown);
    await this.fetchUserProfile();
    await this.fetchPmsConfigs();
    await this.fetchPendingUsers();
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeydown);
  }
};
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.hub-page {
  min-height: 100vh;
  background: #f5f3ff;
  font-family: 'DM Sans', sans-serif;
  color: #12113a;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  position: relative;
  overflow-x: hidden;
}

/* Aurora background */
.hub-page::before,
.hub-page::after {
  content: '';
  position: fixed;
  border-radius: 50%;
  filter: blur(120px);
  z-index: 0;
  pointer-events: none;
}

.hub-page::before {
  width: 620px;
  height: 620px;
  top: -180px;
  right: -160px;
  background: radial-gradient(circle, rgba(128,117,240,0.35) 0%, rgba(128,117,240,0) 70%);
  animation: floatSlow 18s ease-in-out infinite;
}

.hub-page::after {
  width: 540px;
  height: 540px;
  bottom: -200px;
  left: -160px;
  background: radial-gradient(circle, rgba(123,82,200,0.28) 0%, rgba(123,82,200,0) 70%);
  animation: floatSlow 22s ease-in-out infinite reverse;
}

@keyframes floatSlow {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(30px, -20px) scale(1.05); }
}

.hub-topbar,
.hub-content {
  position: relative;
  z-index: 1;
}

/* ── TOPBAR ── */
.hub-topbar {
  background: linear-gradient(180deg, #5b58d4 0%, #4e4bbb 30%, #6b4fd8 70%, #7b52c8 100%);
  border-bottom: none;
  padding: 0 40px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 50;
}

.hub-topbar::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  pointer-events: none;
  background: linear-gradient(90deg,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.15) 15%,
    rgba(255,255,255,0.6) 50%,
    rgba(255,255,255,0.15) 85%,
    rgba(255,255,255,0) 100%);
}

.brand {
  display: flex;
  align-items: center;
}

.brand-logo {
  height: 17px;
  width: auto;
  object-fit: contain;
  display: block;
  filter: brightness(0) invert(1);
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-menu-wrap {
  position: relative;
}

.user-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px 5px 5px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 100px;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
}

.user-chip:hover,
.user-chip.is-open {
  background: rgba(255,255,255,0.2);
  border-color: rgba(255,255,255,0.35);
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255,255,255,0.22);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  font-family: 'Bricolage Grotesque', sans-serif;
  letter-spacing: -0.3px;
  flex-shrink: 0;
}

.user-name {
  font-size: 13px;
  font-weight: 600;
  color: white;
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chev {
  color: rgba(255,255,255,0.75);
  transition: transform 0.15s ease;
  flex-shrink: 0;
}

.user-chip.is-open .chev {
  transform: rotate(180deg);
}

/* ── DROPDOWN MENU ── */
.user-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 240px;
  background: white;
  border: 1px solid #ebe9f8;
  border-radius: 12px;
  box-shadow:
    0 4px 6px rgba(15,14,46,0.04),
    0 12px 32px rgba(15,14,46,0.18);
  padding: 6px;
  z-index: 100;
}

.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.menu-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px 12px;
}

.menu-user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #5b58d4 0%, #7b52c8 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  font-family: 'Bricolage Grotesque', sans-serif;
  letter-spacing: -0.3px;
  flex-shrink: 0;
}

.menu-user-info {
  min-width: 0;
  flex: 1;
}

.menu-user-name {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #12113a;
  letter-spacing: -0.2px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.menu-user-email {
  font-size: 11px;
  color: #9896b8;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.menu-divider {
  height: 1px;
  background: #ebe9f8;
  margin: 4px 0;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: #4a4870;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.12s ease, color 0.12s ease;
  font-family: inherit;
  text-align: left;
}

.menu-item svg {
  color: #9896b8;
  transition: color 0.12s ease;
  flex-shrink: 0;
}

.menu-item:hover {
  background: #f5f3ff;
  color: #12113a;
}

.menu-item:hover svg {
  color: #5b58d4;
}

.menu-item-danger {
  color: #dc2626;
}

.menu-item-danger svg {
  color: #dc2626;
}

.menu-item-danger:hover {
  background: #fef2f2;
  color: #dc2626;
}

.menu-item-danger:hover svg {
  color: #dc2626;
}

/* ── MAIN ── */
.hub-content {
  max-width: 1080px;
  margin: 0 auto;
  padding: 48px 32px 64px;
  animation: fadeIn 0.4s 0.05s both;
}

/* ── HERO ── */
.hero {
  margin-bottom: 40px;
  animation: heroIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 8px;
  background: rgba(255,255,255,0.7);
  border: 1px solid rgba(128,117,240,0.25);
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  color: #5b58d4;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 16px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 2px 12px rgba(91,88,212,0.08);
}

.hero-eyebrow-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #5b58d4;
  box-shadow: 0 0 0 3px rgba(91,88,212,0.18);
}

.hero-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 34px;
  font-weight: 800;
  color: #12113a;
  letter-spacing: -0.9px;
  line-height: 1.1;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #12113a 0%, #5b58d4 55%, #7b52c8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-sub {
  font-size: 16px;
  color: #6b6890;
  font-weight: 400;
  max-width: 560px;
  line-height: 1.5;
}

@keyframes heroIn {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ── QUICK ACTIONS ── */
.quick-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
  margin-bottom: 32px;
  animation: heroIn 0.6s 0.1s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.action-tile {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  background: rgba(255,255,255,0.72);
  border: 1px solid rgba(235,233,248,0.9);
  border-radius: 14px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  text-decoration: none;
  position: relative;
  text-align: left;
  font-family: inherit;
  box-shadow: 0 2px 8px rgba(15,14,46,0.04), inset 0 1px 0 rgba(255,255,255,0.7);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  overflow: hidden;
}

.action-tile::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(135deg, rgba(128,117,240,0) 0%, rgba(128,117,240,0.06) 100%);
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.action-tile:hover {
  border-color: rgba(128,117,240,0.4);
  box-shadow: 0 8px 24px rgba(91,88,212,0.14), inset 0 1px 0 rgba(255,255,255,0.7);
  transform: translateY(-2px);
}

.action-tile:hover::after {
  opacity: 1;
}

.action-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(128,117,240,0.1);
  color: #5b58d4;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.action-tile-admin .action-icon {
  background: rgba(245,158,11,0.12);
  color: #d97706;
}

.action-text {
  flex: 1;
  min-width: 0;
}

.action-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #12113a;
  line-height: 1.2;
  margin-bottom: 2px;
  letter-spacing: -0.2px;
}

.action-desc {
  font-size: 12px;
  color: #9896b8;
}

.action-chevron {
  color: #cfcce8;
  display: flex;
  align-items: center;
  transition: transform 0.15s ease, color 0.15s ease;
}

.action-tile:hover .action-chevron {
  color: #5b58d4;
  transform: translateX(2px);
}

.notification-badge {
  background: #e53e3e;
  color: white;
  font-size: 11px;
  font-weight: 700;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'Bricolage Grotesque', sans-serif;
}

/* ── PMS SECTION ── */
.pms-section {
  background: rgba(255,255,255,0.75);
  border: 1px solid rgba(235,233,248,0.9);
  border-radius: 18px;
  overflow: hidden;
  box-shadow:
    0 1px 3px rgba(15,14,46,0.04),
    0 12px 40px rgba(91,88,212,0.08),
    inset 0 1px 0 rgba(255,255,255,0.7);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  animation: heroIn 0.6s 0.2s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(235,233,248,0.7);
  gap: 14px;
  background: linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 100%);
}

.section-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #12113a;
  letter-spacing: -0.3px;
  line-height: 1.2;
}

.section-sub {
  font-size: 12px;
  color: #9896b8;
  margin-top: 3px;
}

/* ── BUTTONS ── */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 16px;
  background: linear-gradient(180deg, #5b58d4 0%, #4e4bbb 100%);
  color: white;
  border: 1px solid #4e4bbb;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
  white-space: nowrap;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(91,88,212,0.3);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-full {
  width: 100%;
  padding: 12px 16px;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 18px;
  background: #f5f3ff;
  color: #4a4870;
  border: 1px solid #ebe9f8;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}

.btn-secondary:hover:not(:disabled) {
  background: #ebe9f8;
  color: #12113a;
}

.btn-outline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 16px;
  background: white;
  color: #5b58d4;
  border: 1px solid rgba(128,117,240,0.4);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}

.btn-outline:hover:not(:disabled) {
  background: rgba(128,117,240,0.08);
  border-color: #8075f0;
}

.btn-outline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-danger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 18px;
  background: #e53e3e;
  color: white;
  border: 1px solid #dc2626;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
  box-shadow: 0 4px 12px rgba(229,62,62,0.3);
}

.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── PMS GRID ── */
.pms-grid {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pms-card {
  position: relative;
  border: 1px solid transparent;
  border-radius: 12px;
  background: rgba(255,255,255,0.5);
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  opacity: 0;
  animation: cardIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.pms-card:nth-child(1) { animation-delay: 0.28s; }
.pms-card:nth-child(2) { animation-delay: 0.36s; }
.pms-card:nth-child(3) { animation-delay: 0.44s; }
.pms-card:nth-child(4) { animation-delay: 0.52s; }
.pms-card:nth-child(5) { animation-delay: 0.60s; }
.pms-card:nth-child(n+6) { animation-delay: 0.68s; }

@keyframes cardIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.pms-card:hover {
  border-color: rgba(128,117,240,0.35);
  background: white;
  box-shadow:
    0 12px 28px rgba(91,88,212,0.14),
    0 2px 6px rgba(15,14,46,0.05);
  transform: translateY(-2px);
}

.pms-card-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  cursor: pointer;
}

.pms-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(128,117,240,0.16) 0%, rgba(123,82,200,0.16) 100%);
  color: #5b58d4;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.pms-card:hover .pms-icon {
  background: linear-gradient(135deg, #5b58d4 0%, #7b52c8 100%);
  color: white;
  transform: scale(1.05);
  box-shadow: 0 8px 20px rgba(91,88,212,0.35);
}

.pms-info {
  flex: 1;
  min-width: 0;
}

.pms-name {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #12113a;
  letter-spacing: -0.2px;
  margin-bottom: 2px;
  line-height: 1.2;
}

.pms-type {
  font-size: 12px;
  color: #9896b8;
  font-weight: 500;
}

.pms-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 9px;
  border-radius: 100px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.pms-status.active {
  background: #ecfdf5;
  color: #059669;
  border-color: #a7f3d0;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

.pms-arrow {
  color: #cfcce8;
  transition: opacity 0.15s ease, transform 0.15s ease, color 0.15s ease;
  display: flex;
  align-items: center;
}

.pms-card:hover .pms-arrow {
  color: #5b58d4;
  opacity: 0;
  transform: translateX(-4px);
}

.delete-pms-btn {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%) scale(0.9);
  background: white;
  color: #9896b8;
  border: 1px solid #ebe9f8;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.18s ease, transform 0.18s ease, color 0.15s ease, background 0.15s ease, border-color 0.15s ease;
  z-index: 10;
  box-shadow: 0 2px 6px rgba(15,14,46,0.06);
}

.pms-card:hover .delete-pms-btn {
  opacity: 1;
  transform: translateY(-50%) scale(1);
}

.delete-pms-btn:hover {
  color: #e53e3e;
  border-color: #fecaca;
  background: #fef2f2;
}

/* ── EMPTY STATE ── */
.empty-state {
  padding: 72px 24px 64px;
  text-align: center;
}

.empty-icon {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: linear-gradient(135deg, #5b58d4 0%, #7b52c8 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  box-shadow:
    0 16px 40px rgba(91,88,212,0.32),
    0 0 0 8px rgba(128,117,240,0.08),
    0 0 0 16px rgba(128,117,240,0.04);
  animation: pulseHalo 3s ease-in-out infinite;
}

@keyframes pulseHalo {
  0%, 100% {
    box-shadow:
      0 16px 40px rgba(91,88,212,0.32),
      0 0 0 8px rgba(128,117,240,0.08),
      0 0 0 16px rgba(128,117,240,0.04);
  }
  50% {
    box-shadow:
      0 16px 40px rgba(91,88,212,0.4),
      0 0 0 12px rgba(128,117,240,0.1),
      0 0 0 24px rgba(128,117,240,0.05);
  }
}

.empty-state h4 {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: #12113a;
  letter-spacing: -0.3px;
  margin-bottom: 6px;
}

.empty-state p {
  font-size: 13px;
  color: #9896b8;
  margin: 0 auto 20px;
  max-width: 340px;
  line-height: 1.55;
}

/* ── MODALS ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15,14,46,0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.15s ease;
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 60px rgba(15,14,46,0.2);
  animation: slideUp 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid #ebe9f8;
}

.modal-header h3 {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #12113a;
  letter-spacing: -0.3px;
}

.close-btn {
  background: transparent;
  border: none;
  color: #9896b8;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.close-btn:hover {
  background: #f5f3ff;
  color: #12113a;
}

/* Step indicator */
.modal-steps {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 22px;
  background: #fafaff;
  border-bottom: 1px solid #ebe9f8;
}

.step {
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0.5;
  transition: opacity 0.15s;
}

.step.active {
  opacity: 1;
}

.step-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  border: 1.5px solid #ebe9f8;
  color: #9896b8;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Bricolage Grotesque', sans-serif;
}

.step.active .step-num {
  background: linear-gradient(180deg, #5b58d4 0%, #4e4bbb 100%);
  color: white;
  border-color: transparent;
}

.step-label {
  font-size: 12px;
  font-weight: 600;
  color: #12113a;
}

.step-divider {
  flex: 1;
  height: 1px;
  background: #ebe9f8;
}

.modal-body {
  padding: 22px;
}

.step-info {
  font-size: 13px;
  color: #4a4870;
  margin-bottom: 18px;
}

/* PMS Options */
.pms-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.pms-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1.5px solid #ebe9f8;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: white;
}

.pms-option:hover:not(.disabled) {
  border-color: rgba(128,117,240,0.4);
  background: rgba(128,117,240,0.03);
}

.pms-option.selected {
  border-color: #5b58d4;
  background: rgba(128,117,240,0.06);
}

.pms-option.disabled {
  opacity: 0.55;
  cursor: not-allowed;
  background: #fafaff;
}

.option-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(128,117,240,0.1);
  color: #5b58d4;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pms-option.disabled .option-icon {
  background: #f1f5f9;
  color: #94a3b8;
}

.option-name {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #12113a;
}

.option-check {
  color: #5b58d4;
  display: flex;
  align-items: center;
}

.coming-soon {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: #f1f5f9;
  color: #64748b;
  padding: 3px 8px;
  border-radius: 6px;
}

/* Forms */
.form-group {
  margin-bottom: 14px;
}

.form-group label,
.password-form label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #12113a;
  margin-bottom: 6px;
  letter-spacing: 0.01em;
}

.form-input {
  width: 100%;
  padding: 10px 14px;
  border: 1.5px solid #ebe9f8;
  border-radius: 8px;
  font-size: 13px;
  color: #12113a;
  background: white;
  transition: all 0.15s ease;
  font-family: inherit;
}

.form-input::placeholder {
  color: #9896b8;
}

.form-input:focus {
  outline: none;
  border-color: #5b58d4;
  box-shadow: 0 0 0 3px rgba(91,88,212,0.1);
}

.form-actions {
  display: flex;
  gap: 8px;
  margin-top: 20px;
}

.form-actions .btn-secondary,
.form-actions .btn-outline,
.form-actions .btn-primary {
  flex: 1;
}

/* Alerts */
.alert {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 14px;
  border: 1px solid transparent;
  line-height: 1.4;
}

.alert-error {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}

.alert-success {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #059669;
}

/* Spinner */
.loading-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255,255,255,0.35);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}

.btn-outline .loading-spinner,
.btn-secondary .loading-spinner {
  border-color: rgba(91,88,212,0.2);
  border-top-color: #5b58d4;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Account Modal */
.account-modal {
  max-width: 640px;
}

.subsection-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9896b8;
  margin-bottom: 14px;
}

.account-section {
  margin-bottom: 24px;
}

.account-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.info-item {
  padding: 12px 14px;
  background: #f5f3ff;
  border: 1px solid #ebe9f8;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.info-item label {
  font-size: 10px;
  font-weight: 700;
  color: #9896b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0;
}

.info-item span {
  font-size: 13px;
  font-weight: 600;
  color: #12113a;
  word-break: break-word;
}

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  width: fit-content;
  border: 1px solid transparent;
}

.status-badge.approved,
.status-badge.active {
  background: #ecfdf5;
  color: #059669;
  border-color: #a7f3d0;
}

.status-badge.pending {
  background: #fffbeb;
  color: #d97706;
  border-color: #fde68a;
}

.status-badge.inactive {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}

.password-section {
  padding: 20px;
  background: #fafaff;
  border: 1px solid #ebe9f8;
  border-radius: 12px;
}

.password-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 380px;
}

.password-form .form-group {
  margin-bottom: 0;
}

.password-form .btn-primary {
  align-self: flex-start;
  margin-top: 4px;
}

.input-with-toggle {
  position: relative;
  display: flex;
  align-items: center;
}

.input-with-toggle .form-input {
  padding-right: 40px;
}

.toggle-password {
  position: absolute;
  right: 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #9896b8;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: color 0.15s;
}

.toggle-password:hover {
  color: #5b58d4;
}

/* Delete Modal */
.delete-modal {
  max-width: 420px;
}

.warning-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(229,62,62,0.1);
  color: #e53e3e;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.warning-text {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #12113a;
  text-align: center;
  margin-bottom: 6px;
  letter-spacing: -0.3px;
}

.warning-subtext {
  font-size: 13px;
  color: #9896b8;
  text-align: center;
  margin-bottom: 20px;
  line-height: 1.55;
}

.modal-actions {
  display: flex;
  gap: 8px;
  margin-top: 20px;
}

.modal-actions button {
  flex: 1;
}

/* Responsive */
@media (max-width: 768px) {
  .hub-topbar {
    padding: 12px 20px;
    height: auto;
    flex-direction: column;
    gap: 12px;
  }

  .topbar-right {
    width: 100%;
    justify-content: space-between;
  }

  .hub-content {
    padding: 32px 16px 48px;
  }

  .hero-title {
    font-size: 24px;
  }

  .quick-actions {
    grid-template-columns: 1fr;
  }

  .section-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .section-header .btn-primary {
    width: 100%;
  }

  .pms-card-content {
    flex-wrap: wrap;
    gap: 12px;
  }

  .pms-info {
    flex-basis: calc(100% - 56px);
    order: -1;
  }

  .account-info-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column;
  }

  .password-form {
    max-width: 100%;
  }
}
</style>
